import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Building2, Plus, Users, Pencil, Trash2, Power, PowerOff } from 'lucide-react';
import { toast } from 'sonner';

export default function Companies() {
    const { api } = useAuth();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingCompany, setEditingCompany] = useState(null);
    
    const [createFormData, setCreateFormData] = useState({
        company_name: '',
        company_code: '',
        company_description: '',
        admin_name: '',
        admin_email: '',
        admin_password: ''
    });

    const [editFormData, setEditFormData] = useState({
        name: '',
        description: ''
    });

    const fetchCompanies = async () => {
        try {
            const response = await api.get('/companies');
            setCompanies(response.data);
        } catch (error) {
            console.error('Error fetching companies:', error);
            toast.error('Erro ao carregar empresas');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    const resetCreateForm = () => {
        setCreateFormData({
            company_name: '',
            company_code: '',
            company_description: '',
            admin_name: '',
            admin_email: '',
            admin_password: ''
        });
    };

    const handleCloseCreateDialog = () => {
        setCreateDialogOpen(false);
        resetCreateForm();
    };

    const handleOpenEditDialog = (company) => {
        setEditingCompany(company);
        setEditFormData({
            name: company.name,
            description: company.description || ''
        });
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditingCompany(null);
        setEditFormData({ name: '', description: '' });
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        
        try {
            await api.post('/companies', createFormData);
            toast.success('Empresa e administrador criados com sucesso');
            handleCloseCreateDialog();
            fetchCompanies();
        } catch (error) {
            console.error('Error creating company:', error);
            toast.error(error.response?.data?.detail || 'Erro ao criar empresa');
        }
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        
        try {
            await api.put(`/companies/${editingCompany.id}`, editFormData);
            toast.success('Empresa atualizada com sucesso');
            handleCloseEditDialog();
            fetchCompanies();
        } catch (error) {
            console.error('Error updating company:', error);
            toast.error(error.response?.data?.detail || 'Erro ao atualizar empresa');
        }
    };

    const handleToggleActive = async (company) => {
        try {
            await api.put(`/companies/${company.id}`, { is_active: !company.is_active });
            toast.success(`Empresa ${!company.is_active ? 'ativada' : 'desativada'}`);
            fetchCompanies();
        } catch (error) {
            console.error('Error toggling company:', error);
            toast.error('Erro ao alterar estado da empresa');
        }
    };

    const handleDelete = async (companyId) => {
        try {
            await api.delete(`/companies/${companyId}`);
            toast.success('Empresa eliminada com sucesso');
            fetchCompanies();
        } catch (error) {
            console.error('Error deleting company:', error);
            toast.error('Erro ao eliminar empresa');
        }
    };

    if (loading) {
        return (
            <div className="space-y-6" data-testid="companies-loading">
                <div className="flex justify-between items-center">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-10 w-40" />
                </div>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {[...Array(6)].map((_, i) => (
                        <Skeleton key={i} className="h-48 rounded-lg" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="companies-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold font-heading text-slate-900">Empresas</h1>
                    <p className="text-slate-500">Gerencie as instâncias/empresas da plataforma</p>
                </div>
                <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                    <DialogTrigger asChild>
                        <Button
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => setCreateDialogOpen(true)}
                            data-testid="add-company-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Criar Empresa
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="font-heading">Criar Nova Empresa</DialogTitle>
                            <DialogDescription>
                                Crie uma nova empresa/instância e o seu administrador
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleCreate} className="space-y-4">
                            <div className="border-b pb-4">
                                <h4 className="font-medium mb-3">Dados da Empresa</h4>
                                <div className="space-y-3">
                                    <div className="space-y-2">
                                        <Label htmlFor="company_name">Nome da Empresa</Label>
                                        <Input
                                            id="company_name"
                                            placeholder="Ex: Indústria ABC"
                                            value={createFormData.company_name}
                                            onChange={(e) => setCreateFormData({ ...createFormData, company_name: e.target.value })}
                                            required
                                            data-testid="company-name-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="company_code">Código de Acesso</Label>
                                        <Input
                                            id="company_code"
                                            placeholder="Ex: industria-abc"
                                            value={createFormData.company_code}
                                            onChange={(e) => setCreateFormData({ ...createFormData, company_code: e.target.value.toLowerCase().replace(/\s/g, '-') })}
                                            required
                                            data-testid="company-code-input"
                                        />
                                        <p className="text-xs text-slate-500">
                                            Código único usado pelos utilizadores para login
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="company_description">Descrição (opcional)</Label>
                                        <Textarea
                                            id="company_description"
                                            placeholder="Breve descrição da empresa"
                                            value={createFormData.company_description}
                                            onChange={(e) => setCreateFormData({ ...createFormData, company_description: e.target.value })}
                                            rows={2}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h4 className="font-medium mb-3">Administrador da Empresa</h4>
                                <div className="space-y-3">
                                    <div className="space-y-2">
                                        <Label htmlFor="admin_name">Nome do Administrador</Label>
                                        <Input
                                            id="admin_name"
                                            placeholder="Nome completo"
                                            value={createFormData.admin_name}
                                            onChange={(e) => setCreateFormData({ ...createFormData, admin_name: e.target.value })}
                                            required
                                            data-testid="admin-name-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="admin_email">Email do Administrador</Label>
                                        <Input
                                            id="admin_email"
                                            type="email"
                                            placeholder="admin@empresa.com"
                                            value={createFormData.admin_email}
                                            onChange={(e) => setCreateFormData({ ...createFormData, admin_email: e.target.value })}
                                            required
                                            data-testid="admin-email-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="admin_password">Password do Administrador</Label>
                                        <Input
                                            id="admin_password"
                                            type="password"
                                            placeholder="••••••••"
                                            value={createFormData.admin_password}
                                            onChange={(e) => setCreateFormData({ ...createFormData, admin_password: e.target.value })}
                                            required
                                            data-testid="admin-password-input"
                                        />
                                    </div>
                                </div>
                            </div>

                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={handleCloseCreateDialog}>
                                    Cancelar
                                </Button>
                                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="company-save-btn">
                                    Criar Empresa
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Editar Empresa</DialogTitle>
                        <DialogDescription>
                            Atualize os dados da empresa
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleEdit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="edit_name">Nome da Empresa</Label>
                            <Input
                                id="edit_name"
                                value={editFormData.name}
                                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                                required
                                data-testid="edit-company-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="edit_description">Descrição</Label>
                            <Textarea
                                id="edit_description"
                                value={editFormData.description}
                                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                                rows={3}
                                data-testid="edit-company-description"
                            />
                        </div>
                        <div className="p-3 rounded-lg bg-slate-50 text-sm text-slate-600">
                            <p><strong>Código:</strong> {editingCompany?.code}</p>
                            <p className="text-xs text-slate-500 mt-1">O código de acesso não pode ser alterado</p>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={handleCloseEditDialog}>
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="edit-company-save-btn">
                                Guardar
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Companies Grid */}
            {companies.length === 0 ? (
                <Card className="p-12" data-testid="no-companies">
                    <div className="text-center">
                        <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">Nenhuma empresa configurada</h3>
                        <p className="text-slate-500 mb-6 max-w-md mx-auto">
                            Crie a primeira empresa para começar a usar a plataforma multi-tenant.
                        </p>
                        <Button
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => setCreateDialogOpen(true)}
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Criar Primeira Empresa
                        </Button>
                    </div>
                </Card>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {companies.map((company) => (
                        <Card key={company.id} className={`hover:shadow-md transition-shadow ${!company.is_active ? 'opacity-60' : ''}`} data-testid={`company-card-${company.id}`}>
                            <CardHeader className="pb-3">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-lg ${company.is_active ? 'bg-emerald-50' : 'bg-slate-100'}`}>
                                            <Building2 className={`w-5 h-5 ${company.is_active ? 'text-emerald-600' : 'text-slate-400'}`} />
                                        </div>
                                        <div>
                                            <CardTitle className="text-base font-heading">{company.name}</CardTitle>
                                            <CardDescription className="text-xs font-mono">
                                                {company.code}
                                            </CardDescription>
                                        </div>
                                    </div>
                                    <Badge variant={company.is_active ? 'default' : 'secondary'} className={company.is_active ? 'bg-emerald-100 text-emerald-700' : ''}>
                                        {company.is_active ? 'Ativa' : 'Inativa'}
                                    </Badge>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {company.description && (
                                        <p className="text-sm text-slate-500 line-clamp-2">{company.description}</p>
                                    )}
                                    
                                    <div className="flex items-center gap-2 pt-2 border-t flex-wrap">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleOpenEditDialog(company)}
                                            data-testid={`edit-company-${company.id}`}
                                        >
                                            <Pencil className="w-4 h-4 mr-1" />
                                            Editar
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleToggleActive(company)}
                                            data-testid={`toggle-company-${company.id}`}
                                        >
                                            {company.is_active ? (
                                                <><PowerOff className="w-4 h-4 mr-1" /> Desativar</>
                                            ) : (
                                                <><Power className="w-4 h-4 mr-1" /> Ativar</>
                                            )}
                                        </Button>
                                        <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                    data-testid={`delete-company-${company.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4 mr-1" />
                                                    Eliminar
                                                </Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent>
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle>Eliminar Empresa?</AlertDialogTitle>
                                                    <AlertDialogDescription>
                                                        Esta ação não pode ser revertida. Todos os dados da empresa (utilizadores, dispositivos, áreas, leituras) serão permanentemente eliminados.
                                                    </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                                    <AlertDialogAction
                                                        className="bg-red-600 hover:bg-red-700"
                                                        onClick={() => handleDelete(company.id)}
                                                    >
                                                        Eliminar Empresa
                                                    </AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
